import { FieldValidationResult } from 'features/common/hooks/fieldsValidations'
import { isDateValid, DateFormat } from 'features/common/utils/dateFormat'
import { SqlNumericParameter, SqlNumericRangeParameter, SqlParameter, SqlParameterType } from 'features/maps/types'
import { formatPlaceholder, sqlParameterItems } from './queryParametersUtils'
import { isEqual } from 'lodash'
import { normalizeNumericValue } from 'features/common/utils/numericUtils'

export const SQL_PLACEHOLDER_START = '{{'
export const SQL_PLACEHOLDER_END = '}}'
export const SQL_PLACEHOLDER_EMPTY = SQL_PLACEHOLDER_START + SQL_PLACEHOLDER_END

export function isValidSqlName(value: string | undefined): boolean {
  return value ? /^[a-zA-Z_]([a-zA-Z0-9_])*$/.test(value) : false
}

export function isValidSqlPlaceholder(value: string | undefined): boolean {
  return value ? /^\{\{[a-zA-Z_]([a-zA-Z0-9_])*\}\}$/.test(value) : false
}

export interface SqlParameterErrors {
  nameError?: string | false
  sqlNameError?: string | false
  sqlValueError?: string | false
  startSqlNameError?: string | false
  startSqlValueError?: string | false
  endSqlNameError?: string | false
  endSqlValueError?: string | false
  minMaxError?: string | false
  minMissing?: string | false
  maxMissing?: string | false
  missingDefault?: string | false
  stepRequired?: string | false
  valuesRequired?: string | false
}

export type SqlParameterErrorsFormatted = Partial<Record<keyof SqlParameterErrors, string>>

export const validateSqlPlaceholder = (value: string): FieldValidationResult => {
  if (!value.startsWith(SQL_PLACEHOLDER_START) || !value.endsWith(SQL_PLACEHOLDER_END)) {
    return 'builder.errors.invalidSqlNameFormat'
  } else if (value === SQL_PLACEHOLDER_EMPTY) {
    return 'builder.errors.invalidSqlNameEmpty'
  } else if (!isValidSqlPlaceholder(value)) {
    return 'builder.errors.invalidSqlNameChars'
  }
  return false
}

const checkValidDate = (value: string) => {
  return (
    (!value && 'builder.errors.mandatoryValue') ||
    (!isDateValid(value, DateFormat.ISO_YYYY_MM_DD) && 'builder.errors.invalidDate')
  )
}

const checkValidNumber = (value: number) => {
  return !Number.isFinite(value) && 'builder.errors.invalidNumber'
}

export function validateSqlParameter(
  parameter: SqlParameter,
  otherParameters: SqlParameter[]
): [errors: SqlParameterErrors, hasErrors: boolean] {
  const errors: SqlParameterErrors = {}

  if (!parameter.name) {
    errors.nameError = 'builder.errors.mandatoryValue'
  }

  const otherSqlNames = new Set(Array.from(sqlParameterItems(otherParameters)).map(({ sqlName }) => sqlName))

  const checkValidSqlName = (value: string) => {
    return (
      (!value && 'builder.errors.mandatoryValue') ||
      (!isValidSqlName(value) && 'builder.errors.invalidSqlNameChars') ||
      (otherSqlNames.has(value) && 'builder.errors.sqlParameterConflict')
    )
  }

  switch (parameter.type) {
    case SqlParameterType.DateRange:
      errors.startSqlNameError = checkValidSqlName(parameter.start.sqlName)
      errors.endSqlNameError =
        checkValidSqlName(parameter.end.sqlName) ||
        (parameter.end.sqlName === parameter.start.sqlName && 'builder.errors.sqlParameterConflict')

      errors.startSqlValueError = checkValidDate(parameter.start.value)
      errors.endSqlValueError =
        checkValidDate(parameter.end.value) ||
        (parameter.end.value < parameter.start.value && 'builder.errors.endBeforeStart')
      break

    case SqlParameterType.Category:
      errors.sqlNameError = checkValidSqlName(parameter.item.sqlName)
      errors.valuesRequired = parameter.values.length === 0 && 'builder.errors.valuesRequired'
      break

    case SqlParameterType.Numeric:
      errors.sqlNameError = checkValidSqlName(parameter.item.sqlName)
      errors.startSqlValueError = checkValidNumber(parameter.item.value)

      errors.minMissing = checkValidNumber(parameter.min)
      errors.maxMissing = checkValidNumber(parameter.max)
      errors.minMaxError = parameter.max <= parameter.min && 'builder.errors.minMaxError'

      errors.missingDefault = checkValidNumber(parameter.defaultValue)
      errors.stepRequired = parameter.step !== undefined && checkValidNumber(parameter.step)
      break

    case SqlParameterType.NumericRange:
      errors.startSqlNameError = checkValidSqlName(parameter.start.sqlName)
      errors.endSqlNameError =
        checkValidSqlName(parameter.end.sqlName) ||
        (parameter.end.sqlName === parameter.start.sqlName && 'builder.errors.sqlParameterConflict')

      errors.startSqlValueError = checkValidNumber(parameter.start.value)
      errors.minMissing = checkValidNumber(parameter.min)
      errors.maxMissing = checkValidNumber(parameter.max)
      errors.minMaxError = parameter.max <= parameter.min && 'builder.errors.minMaxError'
      errors.stepRequired = parameter.step !== undefined && checkValidNumber(parameter.step)
      break
  }
  const hasErrors = Object.values(errors).some(Boolean)
  return [errors, hasErrors]
}

export function sqlParameterNames(parameter: SqlParameter): string[] {
  return Array.from(sqlParameterItems(parameter)).map(({ sqlName }) => formatPlaceholder(sqlName))
}

/** Format sql placeholders as comma-separated list. */
export function formatSqlParameterPlaceholders(parameter: SqlParameter) {
  return sqlParameterNames(parameter).join(', ')
}

export function sanitizeNameToSqlIdentifier(v: string) {
  return v
    .replace(/^[0-9]+/, '') // no numbers at start of id
    .replaceAll(/[^a-zA-Z0-9_]/g, '_') // all non latin characters and numbers -> _
    .replaceAll(/_+/g, '_') // replace contiguous spans od _
    .replace(/$_+/, '') // leading & trailing _ are also removed
    .replace(/_+^/, '')
    .toLowerCase()
}

export function normalizeSqlPlaceholderValue(value: string) {
  if (!value) {
    return value
  }
  let newValue: string = value || ''
  if (!newValue.startsWith(SQL_PLACEHOLDER_START)) {
    newValue = SQL_PLACEHOLDER_START + value.replaceAll('{', '')
  }
  if (!newValue.endsWith(SQL_PLACEHOLDER_END)) {
    newValue = newValue.replaceAll('}', '') + SQL_PLACEHOLDER_END
  }
  return newValue
}

export function removeSqlPlaceholderSyntax(v: string) {
  return v.replaceAll(/[{}]/g, '')
}

/**
 * Remove temporary runtime values in SQL parameters that shouldn't be saved in backend.
 */
export function removeSqlParameterCustomValues(param: SqlParameter): SqlParameter {
  switch (param.type) {
    case SqlParameterType.Category:
      return {
        ...param,
        item: {
          ...param.item,
          value: param.item.value.filter((i) => param.values.includes(i))
        }
      }
  }
  return param
}

export function sqlPlaceholderNamesChanged(param: SqlParameter, originalParameter: SqlParameter) {
  const newNames = sqlParameterNames(param)
  const oldNames = sqlParameterNames(originalParameter)

  return !isEqual(newNames, oldNames)
}

export function normalizeSqlNumericContraintsAfterUpdate(
  newValue: SqlNumericParameter | SqlNumericRangeParameter,
  oldValue: SqlNumericParameter | SqlNumericRangeParameter
) {
  const fixedValue = { ...newValue }
  const stepChanged = fixedValue.step !== oldValue.step
  if (stepChanged && fixedValue.step) {
    fixedValue.max = normalizeNumericValue(fixedValue.max, {
      step: fixedValue.step,
      min: fixedValue.min + fixedValue.step
    })
  }

  const typeChanged = oldValue.type !== newValue.type
  const minOrMaxChanged = fixedValue.min !== oldValue.min || fixedValue.max !== oldValue.max

  // not sure if swapping min/max makes sense here
  // if (minOrMaxChanged) {
  //   fixedValue.min = Math.min(newValue.min, newValue.max)
  //   fixedValue.max = Math.max(newValue.min, newValue.max)
  // }

  if (typeChanged || minOrMaxChanged || stepChanged) {
    if (fixedValue.type === SqlParameterType.Numeric) {
      fixedValue.defaultValue = normalizeNumericValue(fixedValue.defaultValue, fixedValue)
      fixedValue.item = {
        ...fixedValue.item,
        value: normalizeNumericValue(fixedValue.item.value, fixedValue)
      }
    }
    if (fixedValue.type === SqlParameterType.NumericRange) {
      fixedValue.start = {
        ...fixedValue.start,
        value: normalizeNumericValue(fixedValue.start.value, { ...fixedValue, defaultValue: fixedValue.min })
      }
      fixedValue.end = {
        ...fixedValue.end,
        value: normalizeNumericValue(fixedValue.end.value, { ...fixedValue, defaultValue: fixedValue.max })
      }
    }
  }
  return fixedValue
}

export function safeParseSqlNumericParameter(value: string, param: SqlNumericParameter): number | null {
  return normalizeNumericValue(Number(value), param)
}

export function safeParseSqlNumericRangeParameter(value: string, param: SqlNumericRangeParameter): number | null {
  return normalizeNumericValue(Number(value), param)
}

export function safeParseSqlDateRangeParameter(date: string): string | null {
  if (!isDateValid(date, DateFormat.ISO_YYYY_MM_DD)) return null
  return date
}
